import EnhancedEncryptionIcon from '@mui/icons-material/EnhancedEncryption';
import React from 'react';

import { BlockTypeEnum, Company, TimezoneAbbreviationEnum } from '_lib/api/apiTypes.shim';

import { Box } from '_lib/components/Box/Box';
import { IconButton } from '_lib/components/IconButton/IconButton';
import { List } from '_lib/components/List/List';
import { ListItem } from '_lib/components/ListItem/ListItem';
import { Stack } from '_lib/components/Stack/Stack';
import { Tooltip } from '_lib/components/Tooltip/Tooltip';
import { Typography } from '_lib/components/Typography/Typography';
import { CompanyWindowDisplay } from '_lib/featureComponents/CompanyWindow/CompanyWindowDisplay';
import { useMediaQuery } from '_lib/hooks/useMediaQuery';
import { DateFormatEnum, DateUtils } from '_lib/utils/date.utils';

import type { AvailableWindow } from '../AppointmentBookingPane.types';

type AvailableWindowTimeProps = {
  availableWindow: AvailableWindow;
  timezone: TimezoneAbbreviationEnum;
  wrapTimeZone?: true;
};

const AvailableWindowTime = (props: AvailableWindowTimeProps): React.ReactElement => {
  const { availableWindow, timezone, wrapTimeZone } = props;
  const { blockType, startTime, endTime } = availableWindow.appointmentWindow;

  const startTimeText = DateUtils.format(startTime, DateFormatEnum.Time, { timezone });
  const endTimeText = endTime ? DateUtils.format(endTime, DateFormatEnum.Time, { timezone }) : undefined;

  const isAllDay = blockType === BlockTypeEnum.ALL_DAY;
  const wrapWhiteSpace = wrapTimeZone ? '\n' : ' ';

  // TODO - clean up this? WTF?
  let text = '';
  switch (blockType) {
    case BlockTypeEnum.ALL_DAY:
      text = 'ALL DAY';
      break;
    case BlockTypeEnum.SPECIFIC_TIME:
      text = `${startTimeText}${wrapWhiteSpace}(${timezone})`;
      break;
    case BlockTypeEnum.RANGE:
      text = `${startTimeText} - ${endTimeText}${wrapWhiteSpace}(${timezone})`;
      break;
  }

  return (
    <Typography
      variant={isAllDay ? 'body1Bold' : 'body1'}
      text={text}
      sx={{
        display: 'inline',
        whiteSpace: 'pre-line',
        width: '100%'
      }}
    />
  );
};

export interface AppointmentWindowListProps {
  timezone: TimezoneAbbreviationEnum;
  isManualEmployeeAssignEnabled: Company['isManualEmployeeAssignEnabled'];
  filteredWindows: AvailableWindow[];
  isLoading: boolean;
  onAppointmentAgentHold: (availableWindow: AvailableWindow) => void;
}

export const AppointmentWindowList = (props: AppointmentWindowListProps): React.ReactElement => {
  const { timezone, isManualEmployeeAssignEnabled, filteredWindows, isLoading, onAppointmentAgentHold } = props;

  // Display 'window time' and 'window info' in two columns on large screens, otherwise single column
  const twoColumnView = useMediaQuery((theme) => theme.breakpoints.up('xl'));

  return (
    <>
      {filteredWindows.length === 0 && !isLoading && (
        <Box sx={{ padding: 2 }}>
          <Typography text='No Available Appointment Windows Found' align='center' sx={{ opacity: 0.75 }} />
        </Box>
      )}
      <List>
        {!isLoading &&
          filteredWindows.map((availableWindow) => (
            <ListItem
              divider
              key={availableWindow.appointmentWindow.id}
              secondaryAction={
                <Tooltip title='Hold Appointment' placement='left'>
                  <IconButton onClick={() => onAppointmentAgentHold(availableWindow)}>
                    <EnhancedEncryptionIcon />
                  </IconButton>
                </Tooltip>
              }>
              <Stack direction='row' spacing={2} sx={{ flexDirection: 'row' }}>
                {twoColumnView && (
                  <Box
                    sx={{
                      width: '150px',
                      maxWidth: '150px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyItems: 'center'
                    }}>
                    <AvailableWindowTime availableWindow={availableWindow} timezone={timezone} wrapTimeZone />
                  </Box>
                )}
                <Box>
                  {!twoColumnView && <AvailableWindowTime availableWindow={availableWindow} timezone={timezone} />}
                  <CompanyWindowDisplay
                    highlight
                    includeTrades
                    companyWindow={availableWindow.companyWindow}
                    isManualEmployeeAssignEnabled={isManualEmployeeAssignEnabled}
                  />
                </Box>
              </Stack>
            </ListItem>
          ))}
      </List>
    </>
  );
};
